<template>
  <li class="search-vocabularies">
    <div class="flex">
      <button
        class="search-toggle-sm"
        v-if="!vocabulary.leaf"
        :aria-pressed="isExpanded ? 'true' : 'false'"
        @click="toggleChildren"
      >
        <svg
          class="h-3 w-3"
          focusable="false"
          aria-hidden="true"
          width="12"
          height="12"
        >
          <use v-bind="{ 'xlink:href': '#arrow-down' }" />
        </svg>
      </button>
      <button
        class="flex w-full items-center py-4 text-left leading-none"
        :class="{
          'ml-8': vocabulary.leaf,
          'font-bold': isActive,
        }"
        @click="toggleVocabulary"
        :aria-pressed="isActive ? 'true' : 'false'"
      >
        <span>{{ vocabulary.text }}</span>
        <svg
          v-if="isActive"
          width="13"
          height="13"
          class="ml-2"
          focusable="false"
          aria-hidden="true"
        >
          <use v-bind="{ 'xlink:href': '#close' }"></use>
        </svg>
        <span class="ml-auto mr-2 text-ccc">{{ vocabulary.count }}</span>
      </button>
    </div>
    <ul v-if="isExpanded && vocabulary.children && vocabulary.children.length">
      <filter-vocabulary-component
        v-for="child in vocabulary.children"
        :key="`${kind}-${child.id}`"
        :kind="kind"
        :vocabulary="child"
        :expanded-items="expandedItems"
        @expandChildren="emitExpandChildren"
        aria-role="treeitem"
      />
    </ul>
  </li>
</template>

<script lang="ts">
import { PropType, SetupContext, computed, defineComponent } from 'vue';
import { VocabularyFilter } from '../@types/filter';
import { FilterResultEntry, Vocabulary } from '../@types/vocabulary';
import VocabularyKind from '../@types/vocabularyKind';
import { getFilterProperty, vocabularyFilter } from '../utils/filter';

export default defineComponent({
  name: 'FilterVocabularyComponent',

  props: {
    kind: {
      type: String,
      required: true,
    },
    vocabulary: {
      type: Object as PropType<Vocabulary & FilterResultEntry>,
      required: true,
    },
    expandedItems: {
      type: Array as PropType<string[]>,
      required: true,
    },
  },

  setup(props, context: SetupContext) {
    const toggleVocabulary = (): void => {
      if (props.kind in VocabularyKind) {
        const filterProperty = getFilterProperty(props.kind);

        if (
          vocabularyFilter[filterProperty as keyof VocabularyFilter] &&
          (vocabularyFilter[filterProperty as keyof VocabularyFilter] as any)
            .id === props.vocabulary.id
        ) {
          vocabularyFilter[filterProperty as keyof VocabularyFilter] =
            undefined;
        } else {
          vocabularyFilter[filterProperty as keyof VocabularyFilter] =
            props.vocabulary as Vocabulary;
        }
      }
    };

    const toggleChildren = (): void => {
      context.emit('expandChildren', props.vocabulary.id);
    };

    const emitExpandChildren = (id: number): void => {
      context.emit('expandChildren', id);
    };

    const isExpanded = computed(() => {
      return props.expandedItems.includes(props.vocabulary.id);
    });

    const isActive = computed(() => {
      if (props.kind in VocabularyKind) {
        const filterProperty = getFilterProperty(props.kind);
        return (
          vocabularyFilter[filterProperty as keyof VocabularyFilter] &&
          (vocabularyFilter[filterProperty as keyof VocabularyFilter] as any)
            .id === props.vocabulary.id
        );
      }
      return false;
    });

    return {
      toggleVocabulary,
      isActive,
      isExpanded,
      toggleChildren,
      emitExpandChildren,
    };
  },
});
</script>
